import { z } from "zod";

/**
 * Hosts we are willing to POST a push to.
 *
 * The endpoint arrives from the client and is stored, then the API makes a
 * server-side request to it — so without this an authenticated user could point
 * us at anything reachable from the API's network and use it as a proxy.
 *
 * Suffix match on the host, because every one of these issues per-user
 * subdomains. Add a vendor here when a browser we support starts using it; the
 * failure mode is a rejected subscription, which is visible, rather than a
 * silent SSRF.
 */
export const ALLOWED_PUSH_HOST_SUFFIXES = [
  // Chromium (FCM)
  "fcm.googleapis.com",
  "android.googleapis.com",
  // Firefox
  "push.services.mozilla.com",
  // Safari / WebKit
  "push.apple.com",
  // Edge / Windows
  "notify.windows.com",
  "push.microsoft.com",
] as const;

export const isAllowedPushServiceEndpoint = (endpoint: string): boolean => {
  let url: URL;
  try {
    url = new URL(endpoint);
  } catch {
    return false;
  }

  if (url.protocol !== "https:") return false;

  return ALLOWED_PUSH_HOST_SUFFIXES.some((suffix) => url.host === suffix || url.host.endsWith(`.${suffix}`));
};

/** The push service's own URL, straight off the browser's `PushSubscription`. */
export const WebPushEndpointSchema = z
  .string()
  .refine(isAllowedPushServiceEndpoint, "endpoint must be an HTTPS URL served by a supported browser push service");

/**
 * The frontends that can register a push endpoint.
 *
 * Declared HERE rather than in `@cede/utils` (which re-exports it) because this
 * package cannot import that one — utils depends on types. Keeping the constant
 * and the zod schema in one file is what stops a fourth app source from
 * type-checking everywhere and then being rejected at this boundary alone.
 */
export const APP_SOURCE = {
  HUB: "hub",
  FLOW: "flow",
  ARENA: "arena",
} as const;

export type AppSource = (typeof APP_SOURCE)[keyof typeof APP_SOURCE];

export const AppSourceSchema = z.enum(APP_SOURCE);

export const SaveWebPushSubscriptionBodySchema = z.object({
  endpoint: WebPushEndpointSchema,
  /** Client public key + auth secret — the payload is encrypted to these. */
  p256dh: z.string().min(1),
  auth: z.string().min(1),
  appSource: AppSourceSchema,
});

export const DeleteWebPushSubscriptionBodySchema = z.object({
  endpoint: WebPushEndpointSchema,
});

/** Admin-only: send a real push to one user to prove delivery end to end. */
export const SendTestWebPushBodySchema = z.object({
  hubUserId: z.number().int().positive(),
  appSource: AppSourceSchema.optional(),
  title: z.string().min(1).max(80).optional(),
  body: z.string().min(1).max(200).optional(),
  /**
   * Path the service worker opens on click. Must stay a same-origin path: the
   * worker feeds it to `client.navigate` / `openWindow`, so an absolute URL
   * here is an open redirect out of the app. A leading `//` is rejected too —
   * that is protocol-relative, i.e. another origin.
   */
  url: z
    .string()
    .max(200)
    .regex(/^\/(?!\/)[^\s]*$/, "url must be a same-origin path beginning with a single '/'")
    .optional(),
});

export type SaveWebPushSubscriptionBody = z.infer<typeof SaveWebPushSubscriptionBodySchema>;
export type DeleteWebPushSubscriptionBody = z.infer<typeof DeleteWebPushSubscriptionBodySchema>;
export type SendTestWebPushBody = z.infer<typeof SendTestWebPushBodySchema>;
